import { useState, useCallback } from 'react'
import { unlockAudioPlayback } from '../utils/audioUnlock'
import { useCallStore } from '../stores/callStore'
import { useApi } from './useApi'
import { logger } from '../utils/logger'
import type { CallHistoryRecord } from '../types'

interface PhoneNumber {
  phoneNumber: string
  name?: string
  isActive: boolean
}

interface PhoneNumbersResponse {
  items: PhoneNumber[]
}

interface OutboundCallResult {
  makeOutboundCall: (phoneNumber: string, callerId: string, callbackCallHistoryId?: string | null) => Promise<boolean>
  makeCallbackCall: (callHistory: CallHistoryRecord) => Promise<boolean>
  fetchPhoneNumbers: () => Promise<PhoneNumber[]>
  sendDigits: (digits: string) => boolean
  isDialing: boolean
  error: string | null
  clearError: () => void
}

/**
 * アウトバウンドコール（外線発信）を管理するカスタムフック
 * Phase 2: 発信者番号選択、コールバック発信、電話番号一覧取得
 */
export const useOutboundCall = (): OutboundCallResult => {
  const [isDialing, setIsDialing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const twilioDevice = useCallStore((state) => state.twilioDevice)
  const startOutgoingCall = useCallStore((state) => state.startOutgoingCall)
  const setCallerNumber = useCallStore((state) => state.setCallerNumber)
  const isInCall = useCallStore((state) => state.isInCall)
  const callStatus = useCallStore((state) => state.callStatus)
  const api = useApi()

  /**
   * 外部電話番号へ発信
   */
  const makeOutboundCall = useCallback(async (phoneNumber: string, callerId: string, callbackCallHistoryId: string | null = null): Promise<boolean> => {
    // iOS Safari: タップの同期文脈で音声再生を解錠
    unlockAudioPlayback()
    if (!twilioDevice) {
      setError('Twilio Deviceが初期化されていません')
      return false
    }

    if (!phoneNumber) {
      setError('電話番号を入力してください')
      return false
    }

    if (!callerId) {
      setError('発信者番号を選択してください')
      return false
    }

    // 通話中チェック
    if (isInCall() || ['connecting', 'connected'].includes(callStatus)) {
      setError('通話中は発信できません')
      return false
    }

    setIsDialing(true)
    setError(null)

    try {
      logger.info('外部発信開始:', { phoneNumber, callerId, callbackCallHistoryId })

      const params: Record<string, string> = {
        To: phoneNumber,
        callerId: callerId,
        callType: 'outbound', // 発信であることを明示
      }

      if (callbackCallHistoryId) {
        params.callbackCallHistoryId = callbackCallHistoryId
      }

      const call = await twilioDevice.connect({ params })

      // 発信状態を設定
      startOutgoingCall(call)
      setCallerNumber(phoneNumber)

      // 通話イベントリスナーを設定（disconnect等を正しく処理するため必須）
      const setupCallListeners = useCallStore.getState().setupCallListeners
      if (setupCallListeners) {
        setupCallListeners(call, null)
      }

      logger.info('外部発信成功:', { phoneNumber, callerId })
      return true
    } catch (err) {
      logger.error('外部発信エラー:', err)
      setError((err as Error).message || '発信に失敗しました')
      return false
    } finally {
      setIsDialing(false)
    }
  }, [twilioDevice, startOutgoingCall, setCallerNumber, isInCall, callStatus])

  /**
   * コールバック発信（通話履歴から）
   */
  const makeCallbackCall = useCallback(async (callHistory: CallHistoryRecord): Promise<boolean> => {
    // 着信の場合: from（発信元）に折り返し、to（着信先）を発信者番号として使用
    // 発信の場合: to（発信先）に再発信、from（発信元）を発信者番号として使用
    const targetNumber = callHistory.originalDirection === 'inbound' ? callHistory.from : callHistory.to
    const callerId = callHistory.originalDirection === 'inbound' ? callHistory.to : callHistory.from

    logger.info('コールバック発信:', {
      callHistoryId: callHistory.callHistoryId,
      direction: callHistory.direction,
      targetNumber,
      callerId,
    })

    return await makeOutboundCall(targetNumber!, callerId!, callHistory.callHistoryId)
  }, [makeOutboundCall])

  /**
   * 組織の電話番号一覧を取得
   */
  const fetchPhoneNumbers = useCallback(async (): Promise<PhoneNumber[]> => {
    try {
      const response = await api.get<PhoneNumbersResponse>('/api/phone-numbers')

      if (response.items) {
        // アクティブな番号のみを返す
        const activeNumbers = response.items.filter(n => n.isActive)
        return activeNumbers
      }

      return []
    } catch (err) {
      logger.error('電話番号一覧取得エラー:', err)
      setError('電話番号の取得に失敗しました')
      return []
    }
  }, [api])

  /**
   * DTMFトーンを送信
   */
  const sendDigits = useCallback((digits: string): boolean => {
    const activeCall = useCallStore.getState().activeCall

    if (!activeCall) {
      logger.warn('アクティブな通話がありません')
      return false
    }

    try {
      activeCall.sendDigits(digits)
      logger.debug('DTMFトーン送信:', digits)
      return true
    } catch (err) {
      logger.error('DTMFトーン送信エラー:', err)
      setError('トーンの送信に失敗しました')
      return false
    }
  }, [])

  return {
    makeOutboundCall,
    makeCallbackCall,
    fetchPhoneNumbers,
    sendDigits,
    isDialing,
    error,
    clearError: () => setError(null),
  }
}
